import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Plus, Search, Building2, User, MapPin, ChevronRight, 
  CheckCircle, Circle, X, Trash2, Loader2
} from 'lucide-react';
import { apiRequest } from '../utils/auth';
import { toast } from 'sonner';

export default function PotentialCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const data = await apiRequest('/api/potential-customers');
      setCustomers(data.potential_customers || []);
    } catch (error) {
      toast.error('Failed to load potential customers');
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter(c =>
    c.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.contact_person_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 50) return 'bg-blue-500';
    if (percentage >= 25) return 'bg-yellow-500';
    return 'bg-gray-400';
  };

  const handleViewDetails = async (customerId) => {
    try {
      const data = await apiRequest(`/api/potential-customers/${customerId}`);
      setSelectedCustomer(data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Failed to load customer details');
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this potential customer?')) return;
    try {
      await apiRequest(`/api/potential-customers/${customerId}`, { method: 'DELETE' });
      toast.success('Potential customer deleted');
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  return (
    <Layout>
      <div data-testid="potential-customers-page" className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold text-[#0A2540]">Customer Onboarding Tracker</h2>
            <p className="text-sm text-gray-600 mt-1">Track potential customers through the onboarding process</p>
          </div>
          <Button
            data-testid="add-potential-customer-btn"
            onClick={() => setShowAddModal(true)}
            className="bg-[#0A2540] hover:bg-[#0d2f52]"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Potential Customer
          </Button>
        </div>

        {/* Search */}
        <Card className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              data-testid="search-potential-customers"
              placeholder="Search by company name, contact person, or city..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </Card>

        {/* Customer List */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-4" />
            <p className="text-gray-500">Loading onboarding customers...</p>
          </div>
        ) : filteredCustomers.length === 0 ? (
          <Card className="p-12 text-center">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No potential customers found</p>
            <p className="text-sm text-gray-400 mt-1">Add your first potential customer to start tracking</p>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredCustomers.map((customer) => (
              <Card 
                key={customer._id} 
                data-testid={`customer-card-${customer._id}`}
                className="p-5 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => handleViewDetails(customer._id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Building2 className="w-5 h-5 text-[#0A2540]" />
                      <h3 className="font-semibold text-[#0A2540]">{customer.company_name}</h3>
                      {customer.category && (
                        <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                          {customer.category}
                        </span>
                      )}
                      {customer.onboarding_status === 'Completed' && (
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded">
                          Converted
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-6 text-sm text-gray-600">
                      {customer.contact_person_name && (
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {customer.contact_person_name}
                        </span>
                      )}
                      {customer.city && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {customer.city}{customer.state ? `, ${customer.state}` : ''}
                        </span>
                      )}
                    </div>
                    {/* Progress Bar */}
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-gray-600">Onboarding Progress</span>
                        <span className="font-medium text-[#0A2540]">{customer.progress_percentage}%</span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${getProgressColor(customer.progress_percentage)} transition-all duration-300`}
                          style={{ width: `${customer.progress_percentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid={`delete-customer-${customer._id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCustomer(customer._id);
                      }}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Add Customer Modal */}
        {showAddModal && (
          <AddCustomerModal 
            onClose={() => setShowAddModal(false)} 
            onSuccess={() => {
              setShowAddModal(false);
              fetchCustomers();
            }}
          />
        )}

        {/* Customer Detail Modal */}
        {showDetailModal && selectedCustomer && (
          <CustomerDetailModal
            customer={selectedCustomer}
            onClose={() => {
              setShowDetailModal(false);
              setSelectedCustomer(null);
            }}
            onUpdate={() => {
              handleViewDetails(selectedCustomer._id);
              fetchCustomers();
            }}
          />
        )}
      </div>
    </Layout>
  );
}

function AddCustomerModal({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    company_name: '',
    address: '',
    city: '',
    state: '',
    country: '',
    contact_person_name: '',
    category: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.company_name.trim()) {
      toast.error('Company name is required');
      return;
    }
    setLoading(true);
    try {
      await apiRequest('/api/potential-customers', {
        method: 'POST',
        body: JSON.stringify(formData)
      });
      toast.success('Potential customer added successfully');
      onSuccess();
    } catch (error) {
      toast.error(error.message || 'Failed to add customer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-lg bg-white max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-[#0A2540]">Add Potential Customer</h3>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
              <Input
                data-testid="input-company-name"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                placeholder="Enter company name"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
              <Input
                data-testid="input-contact-person"
                value={formData.contact_person_name}
                onChange={(e) => setFormData({ ...formData, contact_person_name: e.target.value })}
                placeholder="Enter contact person name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                data-testid="select-category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select category</option>
                <option value="OEM">OEM</option>
                <option value="Tier-1">Tier-1</option>
                <option value="Tier-2">Tier-2</option>
                <option value="Aftermarket">Aftermarket</option>
                <option value="Industrial">Industrial</option>
                <option value="Export">Export</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <Input
                data-testid="input-address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Enter address"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <Input
                  data-testid="input-city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="City"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <Input
                  data-testid="input-state"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  placeholder="State"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <Input
                data-testid="input-country"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                placeholder="Country"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading}
                className="flex-1 bg-[#0A2540] hover:bg-[#0d2f52]"
                data-testid="submit-add-customer"
              >
                {loading ? 'Adding...' : 'Add Customer'}
              </Button>
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
}

function CustomerDetailModal({ customer, onClose, onUpdate }) {
  const [steps, setSteps] = useState(customer.onboarding_steps || []);
  const [updatingStep, setUpdatingStep] = useState(null);
  const [editingStep, setEditingStep] = useState(null);
  const [stepRemarks, setStepRemarks] = useState('');
  const [stepDate, setStepDate] = useState('');

  const handleUpdateStep = async (stepNumber, newStatus, remarks = null, date = null) => {
    setUpdatingStep(stepNumber);
    try {
      const response = await apiRequest(`/api/potential-customers/${customer._id}/steps/${stepNumber}`, {
        method: 'PUT',
        body: JSON.stringify({ 
          status: newStatus,
          remarks: remarks,
          completion_date: date
        })
      });
      
      if (response.auto_registered) {
        toast.success(`Customer auto-registered! Code: ${response.customer_code}`);
      } else {
        toast.success('Step updated');
      }
      
      onUpdate();
      // Update local state
      setSteps(prev => prev.map(s => 
        s.step_number === stepNumber 
          ? { ...s, status: newStatus, remarks: remarks || s.remarks, completion_date: date || s.completion_date }
          : s
      ));
      setEditingStep(null);
    } catch (error) {
      toast.error('Failed to update step');
    } finally {
      setUpdatingStep(null);
    }
  };

  const completedCount = steps.filter(s => s.status === 'Yes').length;
  const totalSteps = steps.length;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl bg-white max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-xl font-semibold text-[#0A2540]">{customer.company_name}</h3>
            <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
              {customer.contact_person_name && (
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {customer.contact_person_name}
                </span>
              )}
              {customer.city && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {customer.city}
                </span>
              )}
              {customer.onboarding_status === 'Completed' && (
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                  Converted to Customer
                </span>
              )}
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Progress Summary */}
        <div className="p-4 bg-gray-50 border-b shrink-0">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Overall Progress</span>
            <span className="text-lg font-bold text-[#0A2540]">{customer.progress_percentage}%</span>
          </div>
          <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#0A2540] transition-all duration-300"
              style={{ width: `${customer.progress_percentage}%` }}
            />
          </div>
          <div className="flex justify-between mt-2 text-xs text-gray-500">
            <span>{completedCount} completed (Yes)</span>
            <span>{totalSteps - completedCount} pending (No)</span>
          </div>
        </div>

        {/* Steps List */}
        <div className="flex-1 overflow-y-auto p-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-4">Onboarding Steps</h4>
          <div className="space-y-3">
            {steps.map((step) => (
              <div 
                key={step.step_number}
                data-testid={`step-${step.step_number}`}
                className={`p-4 border rounded-lg transition-all ${
                  step.status === 'Yes' ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex items-start gap-3">
                  {step.status === 'Yes' ? (
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                  ) : (
                    <Circle className="w-5 h-5 text-gray-300 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-gray-900">
                          {step.step_number}. {step.activity}
                        </span>
                        {step.optional && (
                          <span className="ml-2 text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded">
                            Optional
                          </span>
                        )}
                        {step.step_number === 16 && (
                          <span className="ml-2 text-xs px-2 py-0.5 bg-blue-100 text-blue-600 rounded">
                            Auto-registers customer
                          </span>
                        )}
                      </div>
                      <span className={`text-xs px-2 py-1 rounded font-medium ${
                        step.status === 'Yes' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {step.status === 'Yes' ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Department: {step.department}</p>
                    
                    {/* Date and Remarks */}
                    {step.completion_date && (
                      <p className="text-xs text-gray-500 mt-1">
                        Date: {step.completion_date}
                      </p>
                    )}
                    {step.remarks && (
                      <p className="text-xs text-gray-600 mt-2 bg-white p-2 rounded border">
                        Remarks: {step.remarks}
                      </p>
                    )}

                    {/* Edit Step Form */}
                    {editingStep === step.step_number && (
                      <div className="mt-3 p-3 bg-white border rounded space-y-3">
                        <div>
                          <label className="text-xs text-gray-600 block mb-1">Date</label>
                          <Input
                            type="date"
                            value={stepDate}
                            onChange={(e) => setStepDate(e.target.value)}
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-600 block mb-1">Remarks (optional)</label>
                          <Input
                            placeholder="Add remarks..."
                            value={stepRemarks}
                            onChange={(e) => setStepRemarks(e.target.value)}
                            className="text-sm"
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingStep(null)}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleUpdateStep(step.step_number, 'Yes', stepRemarks, stepDate)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            Mark Yes
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    {editingStep !== step.step_number && (
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        {step.status !== 'Yes' ? (
                          <Button
                            size="sm"
                            disabled={updatingStep === step.step_number}
                            onClick={() => {
                              setEditingStep(step.step_number);
                              setStepDate(new Date().toISOString().split('T')[0]);
                              setStepRemarks(step.remarks || '');
                            }}
                            className="text-xs h-7 bg-green-600 hover:bg-green-700"
                          >
                            Mark Yes
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={updatingStep === step.step_number}
                            onClick={() => handleUpdateStep(step.step_number, 'No', step.remarks, null)}
                            className="text-xs h-7"
                          >
                            Mark No
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 shrink-0">
          <Button onClick={onClose} className="w-full">Close</Button>
        </div>
      </Card>
    </div>
  );
}
